import React from 'react';
import LootboxSheet from './LootboxSheet';
import LootboxHero from './LootboxHero';
import { describeReward, rarityColor } from '../utils/lootboxRewards';
import '../styles/chestInfoModal.css';
import { useTranslation } from 'react-i18next';
import { entityText } from '../../../i18n/entityText';

/**
 * Chest popup: chest image + owned counter, possible rewards in one row,
 * footer with "Open chest" (chest + matching key owned) or where to get it.
 * Contents come from the store (GET /v1/lootbox/contents, loaded with the Cases tab).
 */
export default function ChestInfoModal({
  chestType, contents, contentsLoading, chestQty, keyQty, error, onOpen, onClose,
  openLabel,
}) {
  const { t } = useTranslation();
  const name = entityText(t, 'chest_type', chestType.id, 'name', chestType.name);
  const color   = rarityColor(chestType.rarity, '#fff');
  const canOpen = chestQty >= 1 && keyQty >= 1;
  const rewards = contents || [];

  return (
    <LootboxSheet
      title={name}
      titleColor={color}
      onClose={onClose}
      footer={(closeThen) => (
        <>
          {error && <div className="chest-info-error">{error}</div>}
          {canOpen ? (
            <button
              type="button"
              className="app-btn app-btn--green"
              onClick={() => closeThen(onOpen)}
            >
              {openLabel || t('lootbox.open_chest', 'Open chest')}
            </button>
          ) : chestQty < 1 ? (
            <div className="chest-info-footer-hint">
              {t('lootbox.get_chests_hint', 'You can get chests in the Shop or in the Game')}
            </div>
          ) : (
            <div className="chest-info-footer-hint chest-info-footer-hint--warn">
              {t('lootbox.need_key', 'You need a {{key}} to open this chest', {
                key: chestType.keyName
                  ? entityText(t, 'key_type', chestType.keyTypeId, 'name', chestType.keyName)
                  : t('lootbox.matching_key', 'matching key'),
              })}
            </div>
          )}
        </>
      )}
    >
      <LootboxHero
        imageUrl={chestType.imageUrl}
        alt={name}
        color={color}
        quantity={chestQty}
      />

      {/* Drop table — no probabilities, no remaining counts */}
      <div className="chest-info-drops-title">{t('lootbox.possible_rewards', 'Possible Rewards')}</div>

      {contentsLoading && rewards.length === 0 ? (
        <div className="chest-info-loading">{t('common.loading', 'Loading…')}</div>
      ) : rewards.length === 0 ? (
        <div className="chest-info-loading">{t('common.no_data', 'No data available')}</div>
      ) : (
        <div className="chest-info-drops" style={{ '--cols': Math.min(rewards.length, 4) }}>
          {rewards.map((c) => {
            const r = describeReward(c, t);
            return (
              <div key={c.id} className="chest-info-drop-cell">
                <div className="chest-info-drop-icon-wrap">
                  {r.imageUrl
                    ? <img src={r.imageUrl} alt={r.name} className="chest-info-drop-icon" />
                    : <span className="chest-info-drop-icon-placeholder">?</span>}
                </div>
                <span className="chest-info-drop-name">{r.name}</span>
                {r.amount && <span className="chest-info-drop-value">{r.amount}</span>}
              </div>
            );
          })}
        </div>
      )}
    </LootboxSheet>
  );
}
